// Slice 11 — Standard Reports browser.
import { useEffect, useMemo, useState } from "react";
import { BarChart3, Download, Loader2, Play, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { ReportChart } from "./_charts";
import { RangeControl, rangeParams, isoToday, isoDaysAgo } from "./_daterange";

export default function StandardReportsPage() {
  const [defs, setDefs] = useState([]);
  const [active, setActive] = useState(null);     // report def id
  const [rng, setRng] = useState("today");
  const [from, setFrom] = useState(isoToday());
  const [to, setTo] = useState(isoToday());
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/reports/standard").then((r) => {
      setDefs(r.data.reports || []);
      if (!active && r.data.reports?.length) setActive(r.data.reports[0].id);
    }).catch((e) => toast.error(formatErr(e)));
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!active) return;
    if (rng === "custom" && !(from && to)) return;
    run();
  }, [active, rng, from, to]); // eslint-disable-line

  async function run() {
    if (!active) return;
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/reports/standard/run",
        { report_id: active, ...rangeParams(rng, from, to) });
      setResult(r.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  }

  async function exportCsv() {
    if (!result) return;
    try {
      const def = defs.find((d) => d.id === active);
      const r = await api.post("/reports/export.csv", {
        source: def.source, dimensions: def.dimensions,
        measures: def.measures, filters: def.filters || {},
        ...rangeParams(rng, from, to),
      }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${active}-${rng}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch (e) { toast.error(formatErr(e)); }
  }

  const activeDef = useMemo(() => defs.find((d) => d.id === active), [defs, active]);

  return (
    <div className="px-4 sm:px-6 py-6 max-w-7xl" data-testid="standard-reports-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Standard Reports</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Pre-built reports across CDR, conversations and predictive-dialer runs. Click any card to load.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <RangeControl rng={rng} setRng={setRng} from={from} to={to} setFrom={setFrom} setTo={setTo} testid="std-range" />
          <Button variant="outline" onClick={run} disabled={busy || !active} className="gap-1.5">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>}
            Refresh
          </Button>
          <Button onClick={exportCsv} disabled={!result} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5" data-testid="std-export">
            <Download className="w-4 h-4"/> CSV
          </Button>
        </div>
      </div>

      {/* Report cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="std-cards">
        {defs.map((d) => (
          <button
            key={d.id}
            onClick={() => setActive(d.id)}
            data-testid={`std-card-${d.id}`}
            className={`text-left rounded-xl border bg-card p-4 transition-all hover:shadow-md
              ${active === d.id
                ? "border-primary ring-2 ring-primary/30"
                : "border-border hover:border-primary/40"}`}
          >
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-primary"/>
              <h3 className="font-display font-bold text-sm truncate">{d.label}</h3>
              <Badge variant="outline" className="ml-auto text-[10px] uppercase">{d.chart}</Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{d.description}</p>
          </button>
        ))}
      </div>

      {/* Active report */}
      {activeDef && (
        <Card className="p-5" data-testid="std-active-card">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
            <h2 className="font-display text-xl font-bold tracking-tight">{activeDef.label}</h2>
            <div className="text-xs text-muted-foreground">
              source: <span className="font-mono">{activeDef.source}</span> ·
              chart: <span className="font-mono">{activeDef.chart}</span>
            </div>
          </div>
          {busy && (
            <div className="h-[260px] flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground"/>
            </div>
          )}
          {!busy && result && (
            <>
              <ReportChart kind={result.chart} columns={result.columns} rows={result.rows} height={320} />
              {/* Data table */}
              <div className="mt-4 overflow-x-auto rounded-md border border-border" data-testid="std-table">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr>
                      {result.columns.map((c) => (
                        <th key={c.id} className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wider">{c.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.length === 0 && (
                      <tr><td colSpan={result.columns.length} className="px-3 py-6 text-center text-sm text-muted-foreground italic">No rows for this range.</td></tr>
                    )}
                    {result.rows.map((r, i) => (
                      <tr key={i} className="odd:bg-card even:bg-muted/20 border-t border-border/60">
                        {result.columns.map((c) => (
                          <td key={c.id} className="px-3 py-2 font-mono text-xs">{r[c.id] ?? ""}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>
      )}
    </div>
  );
}
